import { useState, useEffect, useCallback } from 'react';
import { Form, Select, InputNumber } from 'antd';
import type { UploadProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import Button from 'components/Base/Button/Button';
import ButtonGroup from 'components/Base/ButtonGroup/ButtonGroup';
import { FormItem } from 'components/Base/Form';
import Checkbox from 'components/Base/Checkbox';
import { DatePicker } from 'components/Base/DatePicker';
import { TextArea, Input } from 'components/Base/Input';
import { EIconNames, Icon } from 'components/Base/Icon';
import Spinner from 'components/Base/Spinner';
import {
  ENotificationType,
  useShowToastNotification,
  useTranslation,
  TRtkErrorType,
  Col,
  Row,
} from '@packages/utils';
import {
  PUBLIC_DISTRIBUTION,
  TInputOutputRow,
  TUpdateInputOutputRequest,
  TCreateInputOutputRequest,
  TSelectedIoFiles,
  TIoSourceType,
} from 'data/types/io.types';
import {
  useUpdateInputOutputMutation,
  useCreateInputOutputMutation,
  useUploadIoDocumentMutation,
  useDeleteIoDocumentsMutation,
} from 'redux/apiSlice/io.slice';
import { inKindApi } from 'redux/apiSlice/inKind.slice';
import { EntitySelector } from 'components/Shared/InputOutputModal/EntitySelector';
import { CategorySelector } from 'components/Shared/InputOutputModal/CategorySelector';
import {
  SModal,
  SUploader,
  SUploadedItemWrapper,
} from 'components/Shared/InputOutputModal/InputOutputModal.styles';
import { SInput } from './EditInputOutputModal.styles';
import { EditInputOutputModalProps } from './EditInputOutputModal.types';

const entityTypeOptions = [
  { label: 'Organization', value: 'ORGANIZATION' },
  { label: 'Person', value: 'PEOPLE' },
  { label: 'ELC', value: 'ELC' },
];

// Outputs logged from an Event or Workshop belong to an event GROW hosts, so
// only these sources can log an event they attended but did not host
// (GROW-4257 FR-5). OutputActions keeps the same list to decide which
// exports carry the external-event columns (GROW-4420).
const EXTERNAL_EVENT_SOURCE_TYPES: TIoSourceType[] = [
  'ELC',
  'ORGANIZATION',
  'PEOPLE',
];

// US ZIP or ZIP+4, the same rule the backend enforces (GROW-4257 FR-6, VR-3)
const ZIP_CODE_PATTERN = /^\d{5}(-\d{4})?$/;

const EXTERNAL_EVENT_TEXT_MAX_LENGTH = 255;

const requiredTrimmedRule = (message: string) => ({
  validator: (_: unknown, value?: string) =>
    value?.trim() ? Promise.resolve() : Promise.reject(new Error(message)),
});

const typeOptions = [
  {
    label: 'Money',
    value: 'MONEY',
    tooltip:
      'Contributions measured in currency (e.g., cash donations, scholarships, grants).',
  },
  {
    label: 'Materials',
    value: 'MATERIALS',
    tooltip:
      'Contributions measured by item count (e.g., books, kits, toys, food, office space).',
  },
  {
    label: 'Time',
    value: 'TIME',
    tooltip:
      'Contributions measured in minutes (e.g., in-kind services, professional coaching, TA time)',
  },
];

const minuteOptions = [
  { label: '00', value: '0' },
  { label: '15', value: '15' },
  { label: '30', value: '30' },
  { label: '45', value: '45' },
];

const ENTITY_TYPE_LABEL_MAP: Record<string, string> = {
  ORGANIZATION: 'Organization',
  PEOPLE: 'Person',
  ELC: 'ELC',
};

type UploadItemRender = NonNullable<UploadProps['itemRender']>;

const EditInputOutputModal = (props: EditInputOutputModalProps) => {
  const {
    mode,
    open,
    sourceType,
    isWorkshopOrEvent = true,
    isVirtual = false,
    hasLocation = true,
    onSuccess,
    onCancel,
  } = props;

  const isEditMode = mode === 'edit';
  const record = isEditMode
    ? (props as { mode: 'edit'; record: TInputOutputRow }).record
    : null;

  // Labels added for GROW-4257 (GROW-4311, FR-14)
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.input-output',
  });
  const theme = useTheme();
  const dispatch = useDispatch();
  const { showToastNotification } = useShowToastNotification();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  // Direction state — toggleable in create, derived from record in edit
  const [direction, setDirection] = useState<'GIVER' | 'RECEIVER'>('GIVER');

  // File upload state (create mode only)
  const [uploadedFile, setUploadedFile] = useState<TSelectedIoFiles | null>(
    null,
  );
  const [isUploading, setIsUploading] = useState(false);

  const [updateInputOutput] = useUpdateInputOutputMutation();
  const [createInputOutput] = useCreateInputOutputMutation();
  const [uploadIoDocument] = useUploadIoDocumentMutation();
  const [deleteIoDocuments] = useDeleteIoDocumentsMutation();

  const selectedType = Form.useWatch('type', form);
  const selectedEntityType = Form.useWatch('entityType', form);
  const isInkindDonation = Form.useWatch('isInkindDonation', form);
  const isExternalEvent = Form.useWatch('isExternalEvent', form);

  const isPublicDistribution =
    direction === 'GIVER' && selectedEntityType === PUBLIC_DISTRIBUTION;
  const isExternalEventSource =
    !isWorkshopOrEvent && EXTERNAL_EVENT_SOURCE_TYPES.includes(sourceType);
  const canLogExternalEvent = direction === 'GIVER' && isExternalEventSource;

  // Derive direction from record data (edit mode)
  const deriveDirection = (row: TInputOutputRow): 'GIVER' | 'RECEIVER' => {
    const { giverType } = row;
    if (giverType === 'HOST' || giverType === 'LOCATION') return 'GIVER';
    if (row.receiverType === 'HOST' || row.receiverType === 'LOCATION')
      return 'RECEIVER';
    return 'GIVER';
  };

  // Pre-populate form when record changes (edit mode)
  useEffect(() => {
    if (!open || !isEditMode || !record) return undefined;

    const dir = deriveDirection(record);
    setDirection(dir);

    // Derive hostOrLocation from the record's giver/receiver types
    const hostSideType =
      dir === 'GIVER' ? record.giverType : record.receiverType;
    const derivedHostOrLocation =
      hostSideType === 'LOCATION' ? 'LOCATION' : 'HOST';

    const otherEntityType =
      dir === 'GIVER' ? record.receiverType : record.giverType;
    const otherEntityId = dir === 'GIVER' ? record.receiverId : record.giverId;

    let moneyValue: string | undefined;
    let materialsQuantity: string | undefined;
    let timeHours: string | undefined;
    let timeMinutes: string | undefined;

    if (record.rawType === 'TIME') {
      if (record.timeHours != null || record.timeMinutes != null) {
        timeHours = String(record.timeHours ?? 0);
        timeMinutes = String(record.timeMinutes ?? 0);
      } else if (record.rawValue) {
        const [h, m] = record.rawValue.split(':');
        timeHours = String(parseInt(h, 10) || 0);
        timeMinutes = String(parseInt(m, 10) || 0);
      }
    } else if (record.rawType === 'MONEY') {
      moneyValue = record.rawValue || undefined;
    } else if (record.rawType === 'MATERIALS') {
      materialsQuantity = record.rawValue || undefined;
    }

    form.setFieldsValue({
      hostOrLocation: derivedHostOrLocation,
      date: record.date ? dayjs(record.date, 'MM/DD/YYYY') : undefined,
      entityType: otherEntityType,
      entity: otherEntityId,
      type: record.rawType,
      category: record.categoryId,
      moneyValue,
      materialsQuantity,
      timeHours,
      timeMinutes,
      isInkindDonation: record.isInkindDonation || false,
      isExternalEvent: !!record.isExternalEvent,
      notes: record.notes,
    });

    // Pending field updates are cancelled if the record changes or the modal
    // closes first.
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Fields shown only for Public Distribution or an external event: set
    // after a tick so their conditional FormItems have mounted (as below).
    if (record.receiverType === PUBLIC_DISTRIBUTION || record.isExternalEvent) {
      timers.push(
        setTimeout(() => {
          form.setFieldsValue({
            familiesServed:
              record.familiesServed != null
                ? String(record.familiesServed)
                : undefined,
            externalEventHost: record.externalEventHost ?? undefined,
            externalEventName: record.externalEventName ?? undefined,
            externalEventZip: record.externalEventZip ?? undefined,
          });
        }, 0),
      );
    }

    // Set inkindDollarValue after a tick so the conditional FormItem has time
    // to mount once isInkindDonation is true
    if (record.isInkindDonation && record.inkindDollarValue != null) {
      timers.push(
        setTimeout(() => {
          form.setFieldsValue({
            inkindDollarValue: record.inkindDollarValue,
          });
        }, 0),
      );
    }

    // Pre-populate uploaded file from record
    if (record.document && record.documentName && record.documentUrl) {
      setUploadedFile({
        name: record.documentName,
        url: record.documentUrl,
        uid: 'existing',
      });
    } else {
      setUploadedFile(null);
    }

    return () => timers.forEach(clearTimeout);
  }, [record, open, form, isEditMode]);

  // Reset form when opening in create mode
  useEffect(() => {
    if (!open || isEditMode) return;
    form.resetFields();
    setDirection('GIVER');
    setUploadedFile(null);
    setIsUploading(false);
    form.setFieldsValue({ hostOrLocation: 'HOST' });
  }, [open, isEditMode, form]);

  // Reset category and value fields when type changes
  useEffect(() => {
    if (!selectedType) return;
    // In edit mode, only reset when switching away from the original type
    if (isEditMode && record && selectedType !== record.rawType) {
      form.setFieldsValue({
        category: undefined,
        moneyValue: undefined,
        materialsQuantity: undefined,
        timeHours: undefined,
        timeMinutes: undefined,
      });
    }
    // In create mode, always clear value fields when type changes
    if (!isEditMode) {
      form.setFieldsValue({
        category: undefined,
        moneyValue: undefined,
        materialsQuantity: undefined,
        timeHours: undefined,
        timeMinutes: undefined,
      });
    }
  }, [selectedType]);

  // Public Distribution names no recipient, and only Public Distribution has a
  // families served figure: clear whichever no longer applies so a hidden value
  // never reaches the payload (GROW-4257 FR-2, FR-9).
  useEffect(() => {
    if (!selectedEntityType) return;
    if (selectedEntityType === PUBLIC_DISTRIBUTION) {
      form.setFieldsValue({ entity: undefined });
    } else {
      form.setFieldsValue({ familiesServed: undefined });
    }
  }, [selectedEntityType]);

  // Public Distribution is a Giving-only option (GROW-4257 D-3)
  useEffect(() => {
    if (
      direction === 'RECEIVER' &&
      form.getFieldValue('entityType') === PUBLIC_DISTRIBUTION
    ) {
      form.setFieldsValue({ entityType: undefined });
    }
  }, [direction]);

  // Reset entity when entity type changes away from original (edit mode)
  useEffect(() => {
    if (!selectedEntityType) return;
    if (isEditMode && record) {
      const dir = deriveDirection(record);
      const originalOtherType =
        dir === 'GIVER' ? record.receiverType : record.giverType;
      if (selectedEntityType !== originalOtherType) {
        form.setFieldsValue({ entity: undefined });
      }
    }
  }, [selectedEntityType]);

  const buildUpdatePayload = (
    values: any,
    original: TInputOutputRow,
  ): TUpdateInputOutputRequest => {
    const payload: TUpdateInputOutputRequest = {};
    const originalDir = deriveDirection(original);

    // Date
    const newDate = values.date?.format('MM/DD/YYYY');
    const originalDate = original.date
      ? dayjs(original.date, 'MM/DD/YYYY').format('MM/DD/YYYY')
      : undefined;
    if (newDate && newDate !== originalDate) {
      payload.ioDate = newDate;
    }

    // If direction changed, we need to swap giver/receiver roles
    if (direction !== originalDir) {
      const hostOrLocation = values.hostOrLocation || 'HOST';
      const hostLocationId =
        hostOrLocation === 'HOST'
          ? (props as any).hostId
          : (props as any).locationId;
      const sourceId = hostLocationId || (props as any).sourceId;
      const resolvedSourceType = isWorkshopOrEvent
        ? hostOrLocation
        : (props as any).sourceType;
      const resolvedSourceId = isWorkshopOrEvent
        ? sourceId
        : (props as any).sourceId;

      if (direction === 'GIVER') {
        payload.giverType = resolvedSourceType;
        payload.giverId = resolvedSourceId;
        payload.receiverType = values.entityType;
        payload.receiverId = values.entity;
      } else {
        payload.giverType = values.entityType;
        payload.giverId = values.entity;
        payload.receiverType = resolvedSourceType;
        payload.receiverId = resolvedSourceId;
      }
    } else {
      // Direction unchanged — check for host/location and entity changes
      const hostOrLocation = values.hostOrLocation || 'HOST';
      const originalHostOrLocation = original.hostOrLocation || 'HOST';
      const hostLocationId =
        hostOrLocation === 'HOST'
          ? (props as any).hostId
          : (props as any).locationId;
      const sourceId = hostLocationId || (props as any).sourceId;

      // Check if host/location side changed (only relevant for workshop/event)
      if (isWorkshopOrEvent && hostOrLocation !== originalHostOrLocation) {
        if (direction === 'GIVER') {
          payload.giverType = hostOrLocation;
          payload.giverId = sourceId;
        } else {
          payload.receiverType = hostOrLocation;
          payload.receiverId = sourceId;
        }
      }

      // Check if entity side changed
      const originalOtherType =
        direction === 'GIVER' ? original.receiverType : original.giverType;
      const originalOtherId =
        direction === 'GIVER' ? original.receiverId : original.giverId;

      if (
        values.entityType !== originalOtherType ||
        values.entity !== originalOtherId
      ) {
        if (direction === 'GIVER') {
          payload.receiverType = values.entityType;
          payload.receiverId = values.entity;
        } else {
          payload.giverType = values.entityType;
          payload.giverId = values.entity;
        }
      }
    }

    // Type
    if (values.type !== original.rawType) {
      payload.type = values.type;
    }

    // Category
    if (values.category !== original.categoryId) {
      payload.categoryId = values.category;
    }

    // Value
    const typeChanged = values.type !== original.rawType;
    let newValue: string | null = null;
    if (values.type === 'MONEY') {
      newValue = values.moneyValue || null;
    } else if (values.type === 'MATERIALS') {
      newValue = values.materialsQuantity || null;
    } else if (values.type === 'TIME') {
      const hours = parseInt(values.timeHours, 10) || 0;
      const minutes = parseInt(values.timeMinutes, 10) || 0;
      newValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
        2,
        '0',
      )}`;

      if (
        hours !== (original.timeHours ?? 0) ||
        minutes !== (original.timeMinutes ?? 0)
      ) {
        payload.timeHours = hours;
        payload.timeMinutes = minutes;
      }
    }

    // When type changed, always send the new value (and clear time fields if
    // switching away from TIME)
    if (typeChanged) {
      if (values.type !== 'TIME') {
        payload.value = newValue ?? undefined;
        // Clear time fields when switching away from TIME
        if (original.rawType === 'TIME') {
          payload.timeHours = 0;
          payload.timeMinutes = 0;
        }
      }
    } else if (
      newValue !== (original.rawValue ?? null) &&
      values.type !== 'TIME'
    ) {
      payload.value = newValue ?? undefined;
    }

    // Notes
    if ((values.notes || '') !== (original.notes || '')) {
      payload.notes = values.notes || '';
    }

    // In-Kind Donation
    const newIsInkind = values.isInkindDonation || false;
    if (newIsInkind !== (original.isInkindDonation || false)) {
      payload.isInkindDonation = newIsInkind;
    }
    if (newIsInkind) {
      const newInkindValue = parseFloat(values.inkindDollarValue) || 0;
      if (newInkindValue !== (original.inkindDollarValue ?? 0)) {
        payload.inkindDollarValue = newInkindValue;
      }
    } else if (original.isInkindDonation) {
      payload.isInkindDonation = false;
      payload.inkindDollarValue = 0;
    }

    // Families served: self-reported, Public Distribution only (GROW-4257
    // FR-9). Always sent when switching to Public Distribution, which the
    // backend requires it for (VR-7). Switching to a named recipient clears it
    // on the backend.
    if (isPublicDistribution) {
      const familiesServed = Number(values.familiesServed);
      if (
        original.receiverType !== PUBLIC_DISTRIBUTION ||
        familiesServed !== original.familiesServed
      ) {
        payload.familiesServed = familiesServed;
      }
    }

    // External event: isExternalEvent=false is the backend's signal to clear
    // the three detail fields (GROW-4257 FR-11). Off once the row is switched
    // to Receiving. On a source that never offers the toggle (Event/Workshop)
    // the stored details are left untouched rather than silently cleared.
    if (isExternalEventSource) {
      const newIsExternalEvent =
        canLogExternalEvent && !!values.isExternalEvent;
      const originalIsExternalEvent = !!original.isExternalEvent;
      const externalEventFields = {
        externalEventHost: values.externalEventHost?.trim(),
        externalEventName: values.externalEventName?.trim(),
        externalEventZip: values.externalEventZip?.trim(),
      };
      const externalEventFieldsChanged = (
        Object.keys(externalEventFields) as (keyof typeof externalEventFields)[]
      ).some(field => externalEventFields[field] !== original[field]);

      if (!newIsExternalEvent) {
        if (originalIsExternalEvent) payload.isExternalEvent = false;
      } else if (!originalIsExternalEvent || externalEventFieldsChanged) {
        // Turning it on or changing any detail: send the flag with all three,
        // as the backend contract recommends
        payload.isExternalEvent = true;
        Object.assign(payload, externalEventFields);
      }
    }

    // Public Distribution names no recipient: a receiverId alongside it is a
    // 400 (VR-1), so never let a stale one through
    if (payload.receiverType === PUBLIC_DISTRIBUTION) {
      delete payload.receiverId;
    }

    // Document
    const originalHasDoc = original.document && original.documentUrl;
    if (uploadedFile) {
      // New or changed file
      if (uploadedFile.url !== original.documentUrl) {
        payload.document = { url: uploadedFile.url, name: uploadedFile.name };
      }
    } else if (originalHasDoc) {
      // File was removed
      payload.document = null;
    }

    return payload;
  };

  const buildCreatePayload = (values: any): TCreateInputOutputRequest => {
    const { sourceId } = props as any;
    const { hostId } = props as any;
    const { locationId } = props as any;

    let normalizedValue: string | null = null;
    let timeHours: number | undefined;
    let timeMinutes: number | undefined;

    if (values.type === 'MONEY') {
      normalizedValue = values.moneyValue || null;
    } else if (values.type === 'MATERIALS') {
      normalizedValue = values.materialsQuantity || null;
    } else if (values.type === 'TIME') {
      const hours = parseInt(values.timeHours, 10) || 0;
      const minutes = parseInt(values.timeMinutes, 10) || 0;
      normalizedValue = `${String(hours).padStart(2, '0')}:${String(
        minutes,
      ).padStart(2, '0')}`;
      timeHours = hours;
      timeMinutes = minutes;
    }

    const getGiverReceiver = () => {
      if (direction === 'GIVER') {
        return {
          giverType: isWorkshopOrEvent
            ? (values.hostOrLocation as 'HOST' | 'LOCATION')
            : sourceType,
          giverId: isWorkshopOrEvent
            ? (values.hostOrLocation === 'HOST' ? hostId : locationId) ||
              sourceId
            : sourceId,
          receiverType: values.entityType,
          // Public Distribution names no recipient (VR-1)
          ...(!isPublicDistribution && { receiverId: values.entity }),
        };
      }
      return {
        giverType: values.entityType,
        giverId: values.entity,
        receiverType: isWorkshopOrEvent
          ? (values.hostOrLocation as 'HOST' | 'LOCATION')
          : sourceType,
        receiverId: isWorkshopOrEvent
          ? (values.hostOrLocation === 'HOST' ? hostId : locationId) || sourceId
          : sourceId,
      };
    };

    const payload: TCreateInputOutputRequest = {
      direction,
      sourceType,
      sourceId,
      ioDate: values.date?.format('MM/DD/YYYY') || null,
      ...getGiverReceiver(),
      type: values.type,
      categoryId: values.category || null,
      value: normalizedValue ?? null,
      notes: values.notes || null,
      ...(values.isInkindDonation && {
        isInkindDonation: true,
        inkindDollarValue: parseFloat(values.inkindDollarValue) || 0,
      }),
      ...(values.type === 'TIME' && {
        timeHours,
        timeMinutes,
      }),
      ...(isPublicDistribution && {
        familiesServed: Number(values.familiesServed),
      }),
      isExternalEvent: !!(canLogExternalEvent && values.isExternalEvent),
      ...(canLogExternalEvent &&
        values.isExternalEvent && {
          externalEventHost: values.externalEventHost.trim(),
          externalEventName: values.externalEventName.trim(),
          externalEventZip: values.externalEventZip.trim(),
        }),
      ...(uploadedFile && {
        document: {
          url: uploadedFile.url,
          name: uploadedFile.name,
        },
      }),
    };

    return payload;
  };

  const handleSave = async () => {
    if (isUploading) return;
    try {
      setIsSaving(true);
      const values = await form.validateFields();

      if (isEditMode && record) {
        // Edit mode
        const payload = buildUpdatePayload(values, record);

        if (Object.keys(payload).length === 0) {
          showToastNotification({
            message: 'No changes to save',
            type: ENotificationType.INFO,
          });
          setIsSaving(false);
          return;
        }

        await updateInputOutput({
          resourceId: record.id,
          body: payload,
        }).unwrap();

        showToastNotification({
          message: 'Output updated successfully',
          type: ENotificationType.SUCCESS,
        });
      } else {
        // Create mode
        const payload = buildCreatePayload(values);
        await createInputOutput(payload).unwrap();

        showToastNotification({
          message: 'Input/Output entry created successfully',
          type: ENotificationType.SUCCESS,
        });
      }

      // Invalidate in-kind caches when the record involves an in-kind donation
      const isInkind =
        values.isInkindDonation || (isEditMode && record?.isInkindDonation);
      if (isInkind) {
        dispatch(
          inKindApi.util.invalidateTags([
            'inKindSummary',
            'inKindQuarterDetail',
          ]),
        );
      }

      onSuccess();
    } catch (error) {
      if ((error as any)?.errorFields) return;

      const status = (error as TRtkErrorType)?.status;
      let message = isEditMode
        ? 'Failed to update output'
        : 'Failed to create Input/Output entry';

      if (status === 403) {
        message = 'You are not authorized to perform this action.';
      } else if (status === 404) {
        message = 'Record not found.';
      } else if ((error as TRtkErrorType)?.data?.message) {
        message = (error as TRtkErrorType)?.data?.message || message;
      }

      showToastNotification({
        message,
        type: ENotificationType.ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = async () => {
    // In create mode, clean up uploaded file
    if (!isEditMode && uploadedFile) {
      try {
        await deleteIoDocuments({
          files: [{ name: uploadedFile.name, url: uploadedFile.url }],
        });
      } catch {
        // Silent fail for cleanup
      }
    }
    setUploadedFile(null);
    setIsUploading(false);
    onCancel();
  };

  // File upload handlers (create mode)
  const handleUpload: UploadProps['customRequest'] = async ({
    file,
    onSuccess: onUploadSuccess,
    onError,
  }) => {
    try {
      setIsUploading(true);
      const data = await uploadIoDocument({ file: file as Blob }).unwrap();

      setUploadedFile({
        ...data,
        uid: (file as any).uid,
      });

      onUploadSuccess?.('ok');
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message || 'Upload failed',
        type: ENotificationType.ERROR,
      });
      onError?.(err as Error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!uploadedFile) return;
    try {
      await deleteIoDocuments({
        files: [{ name: uploadedFile.name, url: uploadedFile.url }],
      }).unwrap();
      setUploadedFile(null);
    } catch {
      showToastNotification({
        message: 'Failed to delete file',
        type: ENotificationType.ERROR,
      });
    }
  };

  const validateUpload = (file: any) => {
    if (uploadedFile) {
      showToastNotification({
        message: 'Only one document per entry is allowed',
        type: ENotificationType.ERROR,
      });
      return false;
    }
    if (file.size / 1024 / 1024 > 10) {
      showToastNotification({
        message: 'File size must be less than 10MB',
        type: ENotificationType.ERROR,
      });
      return false;
    }
    return true;
  };

  const renderUploadItem = useCallback<UploadItemRender>(
    (_, file) => (
      <SUploadedItemWrapper className="custom-file-icon my-2">
        <Row align="middle" wrap={false}>
          <Col md={2}>
            {isUploading ? (
              <Spinner spinnerSize={24} color={theme.colorBlackIconColor} />
            ) : (
              <Icon icon={EIconNames.FILE} color={theme.colorBlackIconColor} />
            )}
          </Col>
          <Col md={20}>
            <span>{file.name}</span>
          </Col>
          <Col md={2}>
            <Button
              disabled={isUploading || isSaving}
              variant="text"
              ghost
              icon={
                <Icon
                  icon={EIconNames.DELETE_OUTLINED}
                  color={theme.colorBlackIconColor}
                  size={24}
                  tooltip={{ title: 'Delete file' }}
                />
              }
              onClick={handleRemoveFile}
            />
          </Col>
        </Row>
      </SUploadedItemWrapper>
    ),
    [isUploading, isSaving],
  );

  // Giving only: materials handed to the general public have no recipient
  // record. "The public gave us something" has no use case (GROW-4257 FR-1,
  // D-3).
  const receiverEntityTypeOptions = [
    ...entityTypeOptions,
    {
      label: t('PUBLIC_DISTRIBUTION', 'Public Distribution'),
      value: PUBLIC_DISTRIBUTION,
    },
  ];

  const hostOrLocationOptions = [
    { label: 'Host', value: 'HOST' },
    {
      label: 'Location',
      value: 'LOCATION',
      disabled: isVirtual || !hasLocation,
    },
  ];

  const renderTypeSpecificFields = () => {
    if (!selectedType) return null;

    switch (selectedType) {
      case 'MONEY':
        return (
          <>
            <FormItem
              label="Category"
              name="category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <CategorySelector
                categoryType="MONEY"
                placeholder="Select category"
              />
            </FormItem>
            <FormItem
              label="Amount ($)"
              name="moneyValue"
              rules={[{ required: true, message: 'Please enter an amount' }]}
            >
              <InputNumber
                placeholder="Enter Amount"
                min={0.01}
                step={0.01}
                precision={2}
                formatter={value =>
                  value
                    ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    : ''
                }
                parser={value => value?.replace(/\$\s?|(,*)/g, '') as any}
                style={{ width: 200 }}
                controls={false}
              />
            </FormItem>
          </>
        );
      case 'MATERIALS':
        return (
          <>
            <FormItem
              label="Category"
              name="category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <CategorySelector
                categoryType="MATERIALS"
                placeholder="Select category"
              />
            </FormItem>
            <FormItem
              label="Quantity"
              name="materialsQuantity"
              rules={[
                { required: true, message: 'Please enter a quantity' },
                { pattern: /^\d+$/, message: 'Please enter a valid quantity' },
              ]}
            >
              <SInput placeholder="Enter Quantity" type="number" min="1" />
            </FormItem>
          </>
        );
      case 'TIME':
        return (
          <>
            <FormItem
              label="Category"
              name="category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <CategorySelector
                categoryType="TIME"
                placeholder="Select category"
              />
            </FormItem>
            <div style={{ display: 'flex', gap: 12 }}>
              <FormItem
                label="Hours"
                name="timeHours"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^\d+$/, message: 'Whole number' },
                ]}
                style={{ flex: 1, minWidth: 0 }}
              >
                <Input
                  placeholder="0"
                  type="number"
                  min="0"
                  style={{ width: '100%', height: 40 }}
                />
              </FormItem>
              <FormItem
                label="Minutes"
                name="timeMinutes"
                rules={[{ required: true, message: 'Required' }]}
                style={{ flex: 1, minWidth: 0 }}
              >
                <Select
                  placeholder="0"
                  options={minuteOptions}
                  style={{ width: '100%', height: 40 }}
                />
              </FormItem>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const modalTitle = isEditMode ? 'Edit Input/Output' : 'Add Input/Output';
  const submitLabel = isEditMode ? 'Save' : 'Add';

  return (
    <SModal
      title={modalTitle}
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      keyboard={false}
      footer={[
        <Button
          key="cancel"
          variant="default"
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || isUploading}
        >
          {submitLabel}
        </Button>,
      ]}
      width={544}
      destroyOnClose
    >
      {/* Direction */}
      <Form layout="vertical">
        <FormItem label="Direction" style={{ marginBottom: 16 }}>
          <ButtonGroup
            options={[
              { label: 'Giving', value: 'GIVER' },
              { label: 'Receiving', value: 'RECEIVER' },
            ]}
            value={direction}
            onChange={value => setDirection(value as 'GIVER' | 'RECEIVER')}
          />
        </FormItem>
      </Form>

      <Form form={form} layout="vertical">
        {/* Host or Location */}
        {isWorkshopOrEvent && (
          <FormItem
            label={direction === 'GIVER' ? 'Who is giving' : 'Who is receiving'}
            name="hostOrLocation"
            style={{ marginBottom: 16 }}
          >
            <ButtonGroup options={hostOrLocationOptions} />
          </FormItem>
        )}

        {/* Activity Date */}
        <FormItem
          label="Activity Date"
          name="date"
          rules={[{ required: true, message: 'Please select a date' }]}
        >
          <DatePicker placeholder="Select date" />
        </FormItem>

        {/* External event: the Activity Date above is the event date, so
            there is no second date field (GROW-4257 FR-4 - FR-6, D-1) */}
        {canLogExternalEvent && (
          <FormItem
            name="isExternalEvent"
            valuePropName="checked"
            style={{ marginBottom: 8 }}
          >
            <Checkbox>
              {t(
                'EXTERNAL_EVENT_TOGGLE',
                'This is an event I attended but did not host',
              )}
            </Checkbox>
          </FormItem>
        )}
        {canLogExternalEvent && isExternalEvent && (
          <>
            <FormItem
              label={t('EXTERNAL_EVENT_HOST', 'Event host')}
              name="externalEventHost"
              rules={[
                requiredTrimmedRule(
                  t(
                    'EXTERNAL_EVENT_HOST_REQUIRED',
                    'Please enter the event host',
                  ),
                ),
              ]}
            >
              <Input
                placeholder={t(
                  'EXTERNAL_EVENT_HOST_PLACEHOLDER',
                  'Enter event host',
                )}
                maxLength={EXTERNAL_EVENT_TEXT_MAX_LENGTH}
              />
            </FormItem>
            <FormItem
              label={t('EXTERNAL_EVENT_NAME', 'Event name')}
              name="externalEventName"
              rules={[
                requiredTrimmedRule(
                  t(
                    'EXTERNAL_EVENT_NAME_REQUIRED',
                    'Please enter the event name',
                  ),
                ),
              ]}
            >
              <Input
                placeholder={t(
                  'EXTERNAL_EVENT_NAME_PLACEHOLDER',
                  'Enter event name',
                )}
                maxLength={EXTERNAL_EVENT_TEXT_MAX_LENGTH}
              />
            </FormItem>
            <FormItem
              label={t('EXTERNAL_EVENT_ZIP', 'Event zip code')}
              name="externalEventZip"
              rules={[
                requiredTrimmedRule(
                  t(
                    'EXTERNAL_EVENT_ZIP_REQUIRED',
                    'Please enter the event zip code',
                  ),
                ),
                {
                  pattern: ZIP_CODE_PATTERN,
                  message: t(
                    'EXTERNAL_EVENT_ZIP_INVALID',
                    'Please enter a valid zip code (e.g. 83440)',
                  ),
                },
              ]}
            >
              <Input
                placeholder={t(
                  'EXTERNAL_EVENT_ZIP_PLACEHOLDER',
                  'Enter zip code',
                )}
                maxLength={10}
              />
            </FormItem>
          </>
        )}

        {/* Entity Type (the "other side") */}
        <FormItem
          label={direction === 'GIVER' ? 'Who is receiving' : 'Who is giving'}
          name="entityType"
          rules={[{ required: true, message: 'Please select an entity type' }]}
        >
          <ButtonGroup
            options={
              direction === 'GIVER'
                ? receiverEntityTypeOptions
                : entityTypeOptions
            }
          />
        </FormItem>

        {/* Families served: self-reported, never derived from Quantity
            (GROW-4257 FR-9) */}
        {isPublicDistribution && (
          <FormItem
            label={t('FAMILIES_SERVED', 'Families served')}
            name="familiesServed"
            // Whole numbers >= 0 only: drop anything that isn't a digit as it
            // is typed or pasted, so 0 is accepted and "12.5"/"-3" never are
            normalize={(value?: string | number) =>
              value == null ? value : String(value).replace(/\D/g, '')
            }
            rules={[
              {
                required: true,
                message: t(
                  'FAMILIES_SERVED_REQUIRED',
                  'Please enter the number of families served',
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'FAMILIES_SERVED_PLACEHOLDER',
                'Enter number of families',
              )}
              inputMode="numeric"
              maxLength={9}
            />
          </FormItem>
        )}

        {/* Entity Selector: Public Distribution names no recipient */}
        {selectedEntityType && !isPublicDistribution && (
          <FormItem
            label={ENTITY_TYPE_LABEL_MAP[selectedEntityType] || 'Entity'}
            name="entity"
            rules={[{ required: true, message: 'Please select an entity' }]}
          >
            <EntitySelector
              key={selectedEntityType}
              entityType={selectedEntityType}
              placeholder={`Select ${
                selectedEntityType === 'PEOPLE'
                  ? 'person'
                  : selectedEntityType?.toLowerCase()
              }`}
            />
          </FormItem>
        )}

        {/* Type */}
        <FormItem
          label="Type"
          name="type"
          rules={[{ required: true, message: 'Please select a type' }]}
        >
          <ButtonGroup options={typeOptions} />
        </FormItem>

        {/* Type-Specific Fields */}
        {renderTypeSpecificFields()}

        {/* In-Kind Donation */}
        <FormItem
          name="isInkindDonation"
          valuePropName="checked"
          style={{ marginBottom: 8 }}
        >
          <Checkbox style={{ alignItems: 'flex-start' }}>
            Is In-Kind Donation (Only non-federal funds can be reported as
            in-kind)
          </Checkbox>
        </FormItem>
        {isInkindDonation && (
          <FormItem
            label="In-Kind Donation Amount ($)"
            name="inkindDollarValue"
            rules={[
              { required: true, message: 'Please enter a dollar value' },
              {
                validator: (_, value) => {
                  if (value != null && value < 1) {
                    return Promise.reject(
                      new Error('Value must be at least $1.00'),
                    );
                  }
                  if (value != null && value > 9999999999.99) {
                    return Promise.reject(
                      new Error('Value cannot exceed $9,999,999,999.99'),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder="Enter Dollar Value"
              min={0.01}
              max={9999999999.99}
              step={0.01}
              precision={2}
              formatter={value =>
                value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
              }
              parser={value => value?.replace(/\$\s?|(,*)/g, '') as any}
              style={{ width: 200 }}
              controls={false}
            />
          </FormItem>
        )}

        {/* Notes */}
        <FormItem label="Note or description" name="notes">
          <TextArea placeholder="Add text" maxLength={350} showCount rows={4} />
        </FormItem>
      </Form>

      {/* Document section */}
      <FormItem style={{ marginTop: 24 }}>
        {uploadedFile ? (
          <SUploadedItemWrapper className="custom-file-icon my-2">
            <Row align="middle" wrap={false}>
              <Col md={2}>
                {isUploading ? (
                  <Spinner spinnerSize={24} color={theme.colorBlackIconColor} />
                ) : (
                  <Icon
                    icon={EIconNames.FILE}
                    color={theme.colorBlackIconColor}
                  />
                )}
              </Col>
              <Col md={20}>
                <span>{uploadedFile.name}</span>
              </Col>
              <Col md={2}>
                <Button
                  disabled={isUploading || isSaving}
                  variant="text"
                  ghost
                  icon={
                    <Icon
                      icon={EIconNames.DELETE_OUTLINED}
                      color={theme.colorBlackIconColor}
                      size={24}
                      tooltip={{ title: 'Delete file' }}
                    />
                  }
                  onClick={handleRemoveFile}
                />
              </Col>
            </Row>
          </SUploadedItemWrapper>
        ) : (
          <SUploader
            disabled={isUploading || isSaving}
            listType="picture"
            itemRender={renderUploadItem}
            fileList={[]}
            maxCount={1}
            multiple={false}
            customRequest={handleUpload}
            beforeUpload={validateUpload}
          >
            <p className="ant-upload-drag-icon">
              <PlusOutlined />
            </p>
            <p className="ant-upload-text">Upload document</p>
            <p className="ant-upload-hint">One document per entry</p>
          </SUploader>
        )}
      </FormItem>
    </SModal>
  );
};

export default EditInputOutputModal;
